import type { AccommodationsScenario } from '@shared/types/Accommodations.types';
import type { FeaturedPlacementVariant } from './hooks/useFeaturedPlacements.types';

/**
 * The scenarios inside the featured-placement experiment: they read the flag and send a
 * variant-derived `maxSponsoredSlots`.
 *
 * BOTH consoles behind the hotel-search URL, not just one. `/:eventHandle/hotel/search` renders
 * `guest` only when the visitor arrived with `?source=guest_map`; every other entry to the SAME url
 * falls through to `ota` (see `Guest.tsx`, `HotelSearchRoute`). Testing `guest` alone would have
 * measured whichever slice of hotel-search traffic happens to come via the guest site's map, and
 * left the rest on the server's contracted sponsored inventory — a control group nobody chose.
 *
 * Both render the featured units: the treatments hang off `ListingsPanel`'s `featuredPlacement`
 * slot, which the shared console passes for every scenario, rather than off either scenario's own
 * tile.
 *
 * `admin` is deliberately absent — see `NO_SPONSORED_SCENARIOS`. `cashFund` and `honeymoon` are
 * different products, not this search.
 */
export const FEATURED_PLACEMENT_SCENARIOS: ReadonlyArray<AccommodationsScenario> = ['guest', 'ota'];

export const isFeaturedPlacementScenario = (scenario: AccommodationsScenario): boolean => FEATURED_PLACEMENT_SCENARIOS.includes(scenario);

/**
 * Scenarios that must never show sponsored placements.
 *
 * `admin` is the couple's own planning tool, not an ad surface — a host picking hotels for their
 * wedding website should not be sold to. `honeymoonAdmin` is the couple's own honeymoon search on
 * their event, so the same applies. `cashFund` and `honeymoon` are different products that this
 * search experiment has no business changing. None of the four is in the experiment: they read no
 * flag and record no exposure, they simply always ask for zero.
 *
 * NOTE this is a behaviour change for all three: before `maxSponsoredSlots` existed they omitted the
 * field and showed whatever sponsored inventory the server's contract allowed.
 */
const NO_SPONSORED_SCENARIOS: ReadonlyArray<AccommodationsScenario> = ['admin', 'cashFund', 'honeymoon', 'honeymoonAdmin'];

/**
 * Variant → `maxSponsoredSlots`, the number of sponsored slots the search asks the BE to fill.
 *
 * `control`'s 0 is deliberate: the baseline arm is a true no-paid-placement page, NOT "today's
 * page". That is a product decision, not a default.
 *
 * `row`'s 4 is a CEILING, not a guarantee: the BE caps the request at the contracted slot count, so
 * a smaller contract quietly returns fewer cards. That gap is what the `sponsoredSlotsRequested` /
 * `sponsoredResultCount` fields on the `destinationSearched` telemetry event exist to measure.
 *
 * ON `0` VERSUS OMITTING THE FIELD — this used to be the load-bearing distinction here, and it is
 * not one any more. The shipped prod schema reads: "Omit (or send 0) to receive no sponsored
 * properties at all — send 10 to ask for the full configured count." So omitting no longer hands the
 * decision back to the server's contracted count; it means zero, exactly like `0`. Every surface
 * therefore states its number outright rather than leaning on omission, which keeps our intent
 * legible and survives the contract moving again.
 */
export const FEATURED_PLACEMENT_MAX_SPONSORED_SLOTS: Readonly<Record<FeaturedPlacementVariant, number>> = {
  control: 0,
  hero: 1,
  row: 4,
};

/**
 * The single place that decides what a search asks for, across all five scenarios.
 *
 * Every scenario now resolves to an explicit number:
 *
 *   - `guest` / `ota` — the arm decides (0 / 1 / 4). `variant` is only ever defined for these.
 *   - `admin`, `cashFund`, `honeymoon` — always `0`. Not in the experiment, never sponsored.
 *
 * An unresolved variant on `guest` / `ota` also asks for `0`, which is a change of meaning worth
 * stating: it used to omit the field to leave the server's count in charge, and under the shipped
 * contract omitting means zero anyway. So a guest whose flag has not resolved sees no sponsored
 * hotels rather than a page the experiment never assigned. That fails closed — it under-serves
 * inventory rather than showing unbucketed guests ads that no arm can be held accountable for.
 */
export const resolveMaxSponsoredSlots = (scenario: AccommodationsScenario, variant: FeaturedPlacementVariant | undefined): number => {
  if (NO_SPONSORED_SCENARIOS.includes(scenario)) return 0;
  if (!isFeaturedPlacementScenario(scenario) || variant === undefined) return 0;

  return FEATURED_PLACEMENT_MAX_SPONSORED_SLOTS[variant];
};
